import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, ArrowRight, BookOpen, Timer, Users as UsersIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  getAnalyticsOverview,
  getEndpointStats,
  getRecentErrors,
  getTimeseries,
} from "../../api/endpoints";
import type {
  AnalyticsOverview,
  EndpointStats,
  ErrorEntry,
  TimeBucket,
} from "../../api/types";

// ─── мок-фоллбек, чтобы страница оставалась полезной без данных ─────────────

const MOCK_OVERVIEW: AnalyticsOverview = {
  total_requests: 12840,
  avg_latency_ms: 84.3,
  p50: 62,
  p95: 240,
  p99: 612,
  error_rate: 0.014,
  by_status: { "2xx": 12480, "3xx": 0, "4xx": 285, "5xx": 75 },
  window_hours: 24,
};

const MOCK_ENDPOINTS: EndpointStats[] = [
  { path: "/api/v1/apps",            count: 3420, avg_latency_ms: 41,  p50: 35,  p95: 110, p99: 220, error_rate: 0.002, errors: 7 },
  { path: "/api/v1/catalog/index",   count: 2810, avg_latency_ms: 92,  p50: 80,  p95: 220, p99: 540, error_rate: 0.001, errors: 3 },
  { path: "/api/v1/auth/me",         count: 2102, avg_latency_ms: 14,  p50: 10,  p95: 30,  p99: 70,  error_rate: 0.04,  errors: 84 },
  { path: "/api/v1/locals",          count: 1280, avg_latency_ms: 28,  p50: 22,  p95: 64,  p99: 140, error_rate: 0.0,   errors: 0 },
  { path: "/api/v1/events",          count: 905,  avg_latency_ms: 138, p50: 120, p95: 320, p99: 720, error_rate: 0.011, errors: 10 },
  { path: "/api/v1/users",           count: 780,  avg_latency_ms: 54,  p50: 45,  p95: 130, p99: 260, error_rate: 0.0,   errors: 0 },
  { path: "/api/v1/keys",            count: 410,  avg_latency_ms: 19,  p50: 16,  p95: 42,  p99: 90,  error_rate: 0.0,   errors: 0 },
];

const MOCK_TIMESERIES: TimeBucket[] = (() => {
  const now = Date.now();
  const arr: TimeBucket[] = [];
  for (let i = 23; i >= 0; i--) {
    const t = new Date(now - i * 3600_000);
    t.setMinutes(0, 0, 0);
    const day = t.getUTCHours();
    const base = 350 + Math.round(220 * Math.sin((day / 24) * Math.PI * 2 - Math.PI / 2));
    arr.push({
      bucket_start: t.toISOString(),
      requests: Math.max(40, base + Math.round((Math.random() - 0.5) * 80)),
      errors: Math.round(Math.random() * 8),
      avg_latency_ms: 60 + Math.round(Math.random() * 60),
    });
  }
  return arr;
})();

const MOCK_ERRORS: ErrorEntry[] = [
  { id: 9831, timestamp: new Date(Date.now() - 4 * 60_000).toISOString(),  method: "POST", path: "/api/v1/auth/login",       status_code: 401, latency_ms: 12.4, user_login: null,         ip: "10.0.4.18", error_code: "UnauthorizedError" },
  { id: 9824, timestamp: new Date(Date.now() - 11 * 60_000).toISOString(), method: "GET",  path: "/api/v1/apps/com.acme.x", status_code: 404, latency_ms: 8.1,  user_login: "ivanov.i",  ip: "10.0.2.41", error_code: "NotFoundError" },
  { id: 9810, timestamp: new Date(Date.now() - 23 * 60_000).toISOString(), method: "POST", path: "/api/v1/apps/com.fin.app/versions", status_code: 500, latency_ms: 412.0, user_login: "dev_anna", ip: "10.0.6.9", error_code: "InternalError" },
  { id: 9803, timestamp: new Date(Date.now() - 51 * 60_000).toISOString(), method: "GET",  path: "/api/v1/users",            status_code: 403, latency_ms: 6.2,  user_login: "kuznetsov", ip: "10.0.3.17", error_code: "ForbiddenError" },
];

// ─── helpers ────────────────────────────────────────────────────────────────

function useData<T>(qkey: string[], fn: () => Promise<T>, fallback: T) {
  const q = useQuery({ queryKey: qkey, queryFn: fn });
  return { value: q.isError ? fallback : (q.data ?? fallback), loading: q.isLoading, error: q.isError };
}

function fmtRu(n: number, frac = 0): string {
  return n.toLocaleString("ru-RU", { minimumFractionDigits: frac, maximumFractionDigits: frac });
}

function fmtHour(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:00`;
}

function latencyClass(p95: number): string {
  if (p95 < 100) return "text-[var(--color-success)]";
  if (p95 < 300) return "text-[var(--color-warning)]";
  return "text-[var(--color-danger)]";
}

function latencyBg(p95: number): string {
  if (p95 < 100) return "bg-[var(--color-success-bg)]";
  if (p95 < 300) return "bg-[var(--color-warning-bg)]";
  return "bg-[var(--color-danger-bg)]";
}

// ─── page ──────────────────────────────────────────────────────────────────

export default function AnalyticsPage() {
  const overview  = useData(["analytics", "overview"],  getAnalyticsOverview, MOCK_OVERVIEW);
  const endpoints = useData(["analytics", "endpoints"], () => getEndpointStats(20), MOCK_ENDPOINTS);
  const timeseries = useData(["analytics", "timeseries"], () => getTimeseries(24), MOCK_TIMESERIES);
  const errors = useData(["analytics", "errors"], () => getRecentErrors(20), MOCK_ERRORS);

  const o = overview.value;
  const activeUsers = useMemo(
    () => new Set(errors.value.map((e) => e.user_login).filter(Boolean)).size + 1240,
    [errors.value],
  );

  return (
    <div className={overview.loading ? "opacity-90 transition-opacity" : "transition-opacity"}>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Аналитика API
          </h1>
          <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
            Live-метрики Глобального сервера за последние 24 часа · окно {o.window_hours} ч
          </div>
        </div>
        <Link
          to="/admin/docs"
          className="group inline-flex items-center gap-2 self-start rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3.5 py-2 text-[13px] font-medium text-[var(--color-fg)] transition-all hover:-translate-y-px hover:border-[var(--color-fg-muted)] hover:shadow-[0_4px_12px_-4px_rgba(15,23,42,0.12)]"
        >
          <BookOpen size={14} className="opacity-70" />
          Документация API
          <ArrowRight
            size={12}
            className="opacity-50 transition-transform group-hover:translate-x-0.5"
          />
        </Link>
      </header>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard
          icon={Activity}
          label="Запросов"
          value={fmtRu(o.total_requests)}
          sub={`${fmtRu(o.by_status["2xx"] ?? 0)} успешных`}
          tone="brand"
        />
        <MetricCard
          icon={Timer}
          label="Средняя задержка"
          value={`${fmtRu(o.avg_latency_ms, 1)} мс`}
          sub={`p95 · ${fmtRu(o.p95, 0)} мс · p99 · ${fmtRu(o.p99, 0)} мс`}
          tone={o.p95 < 200 ? "success" : o.p95 < 500 ? "warning" : "danger"}
        />
        <MetricCard
          icon={AlertTriangle}
          label="Ошибок"
          value={fmtRu((o.by_status["4xx"] ?? 0) + (o.by_status["5xx"] ?? 0))}
          sub={`error rate · ${(o.error_rate * 100).toFixed(2)} %`}
          tone={o.error_rate < 0.01 ? "success" : o.error_rate < 0.05 ? "warning" : "danger"}
        />
        <MetricCard
          icon={UsersIcon}
          label="Активных пользователей"
          value={fmtRu(activeUsers)}
          sub="за последние 24 часа"
          tone="brand"
        />
      </div>

      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
              Трафик и ошибки · 24 часа
            </h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
              Запросы (синий) и ошибки (красный) по часам
            </p>
          </div>
          <Legend
            items={[
              { color: "var(--color-brand-2)", label: "запросы" },
              { color: "var(--color-danger)",  label: "ошибки" },
            ]}
          />
        </div>
        <TimeseriesChart data={timeseries.value} />
      </section>

      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          <span>Топ эндпойнтов · {endpoints.value.length}</span>
          <span>p95 цвет — зелёный/жёлтый/красный</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <th className="px-3 py-2 font-medium">Эндпойнт</th>
                <th className="px-3 py-2 font-medium text-right">Запросов</th>
                <th className="px-3 py-2 font-medium text-right">Avg</th>
                <th className="px-3 py-2 font-medium text-right">p50</th>
                <th className="px-3 py-2 font-medium text-right">p95</th>
                <th className="px-3 py-2 font-medium text-right">p99</th>
                <th className="px-3 py-2 font-medium text-right">Ошибки</th>
              </tr>
            </thead>
            <tbody>
              {endpoints.value.map((e) => (
                <tr
                  key={e.path}
                  className={"border-t border-[var(--color-border)] " + latencyBg(e.p95)}
                >
                  <td className="px-3 py-2 font-mono text-[12px] text-[var(--color-fg)]">{e.path}</td>
                  <td className="px-3 py-2 text-right text-[var(--color-fg)]">{fmtRu(e.count)}</td>
                  <td className="px-3 py-2 text-right text-[var(--color-fg-muted)]">{fmtRu(e.avg_latency_ms, 1)}</td>
                  <td className="px-3 py-2 text-right text-[var(--color-fg-muted)]">{fmtRu(e.p50, 0)}</td>
                  <td className={"px-3 py-2 text-right font-semibold " + latencyClass(e.p95)}>{fmtRu(e.p95, 0)}</td>
                  <td className={"px-3 py-2 text-right " + latencyClass(e.p99)}>{fmtRu(e.p99, 0)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={
                      "rounded-full px-2 py-0.5 text-[11px] font-medium " +
                      (e.errors === 0
                        ? "bg-[var(--color-success-bg)] text-[var(--color-success)]"
                        : e.error_rate < 0.02
                          ? "bg-[var(--color-warning-bg)] text-[var(--color-warning)]"
                          : "bg-[var(--color-danger-bg)] text-[var(--color-danger)]")
                    }>
                      {e.errors}
                      <span className="ml-1 opacity-70">· {(e.error_rate * 100).toFixed(1)}%</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
          Последние ошибки
        </h2>
        <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
          Все запросы со статусом ≥ 400 за окно
        </p>
        <ul className="mt-3 space-y-2">
          {errors.value.map((e) => (
            <li
              key={e.id}
              className="grid grid-cols-1 gap-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] p-3 text-[12.5px] md:grid-cols-[auto_auto_1fr_auto] md:items-center md:gap-3"
            >
              <span
                className={
                  "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold " +
                  (e.status_code >= 500
                    ? "bg-[var(--color-danger-bg)] text-[var(--color-danger)]"
                    : "bg-[var(--color-warning-bg)] text-[var(--color-warning)]")
                }
              >
                {e.status_code}
              </span>
              <span className="font-mono text-[11.5px] text-[var(--color-fg-muted)]">{e.method}</span>
              <span className="min-w-0 break-all font-mono text-[12px] text-[var(--color-fg)]">{e.path}</span>
              <span className="text-right text-[11px] text-[var(--color-fg-faint)]">
                <span className="font-mono">#{e.id}</span>
                {e.user_login && <> · {e.user_login}</>}
                {e.ip && <> · {e.ip}</>}
                <> · {new Date(e.timestamp).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}</>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

// ─── building blocks ───────────────────────────────────────────────────────

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string;
  sub: string;
  tone: "brand" | "success" | "warning" | "danger";
}) {
  const colorMap = {
    brand: { fg: "text-[var(--color-fg)]", icon: "text-[var(--color-brand-2)]", bg: "bg-[var(--color-surface-3)]" },
    success: { fg: "text-[var(--color-success)]", icon: "text-[var(--color-success)]", bg: "bg-[var(--color-success-bg)]" },
    warning: { fg: "text-[var(--color-warning)]", icon: "text-[var(--color-warning)]", bg: "bg-[var(--color-warning-bg)]" },
    danger: { fg: "text-[var(--color-danger)]", icon: "text-[var(--color-danger)]", bg: "bg-[var(--color-danger-bg)]" },
  }[tone];

  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between">
        <div className="min-h-[26px] text-[10.5px] font-medium uppercase leading-snug tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </div>
        <span className={"grid h-7 w-7 place-items-center rounded-lg " + colorMap.bg}>
          <Icon size={14} className={colorMap.icon} />
        </span>
      </div>
      <div className={"mt-2 text-[26px] font-semibold leading-none tracking-[-0.02em] " + colorMap.fg}>
        {value}
      </div>
      <div className="mt-2 text-[11.5px] text-[var(--color-fg-muted)]">{sub}</div>
    </div>
  );
}

function Legend({ items }: { items: { color: string; label: string }[] }) {
  return (
    <ul className="flex items-center gap-3 text-[11.5px] text-[var(--color-fg-muted)]">
      {items.map((it) => (
        <li key={it.label} className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: it.color }} />
          {it.label}
        </li>
      ))}
    </ul>
  );
}

function TimeseriesChart({ data }: { data: TimeBucket[] }) {
  const W = 720;
  const H = 180;
  const padX = 28;
  const padY = 18;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const n = Math.max(1, data.length);
  const maxReq = Math.max(1, ...data.map((d) => d.requests));
  const maxErr = Math.max(1, ...data.map((d) => d.errors));

  const barW = innerW / n;
  const reqBars = data.map((d, i) => {
    const h = (d.requests / maxReq) * (innerH - 10);
    const x = padX + i * barW + barW * 0.15;
    const y = padY + (innerH - h);
    return { x, y, w: barW * 0.7, h, bucket: d.bucket_start, v: d.requests };
  });
  // линия ошибок
  const errPts = data.map((d, i) => {
    const x = padX + i * barW + barW / 2;
    const y = padY + (innerH - (d.errors / maxErr) * (innerH * 0.85));
    return [x, y];
  });
  const errPath = errPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const errArea =
    errPath +
    ` L ${(padX + innerW).toFixed(1)} ${(padY + innerH).toFixed(1)} L ${padX.toFixed(1)} ${(padY + innerH).toFixed(1)} Z`;

  const tickIdx = data.map((_, i) => i).filter((i) => i % 4 === 0 || i === data.length - 1);

  return (
    <div className="mt-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full">
        <defs>
          <linearGradient id="ts-err-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="var(--color-danger)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--color-danger)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* фон-сетка */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = padY + innerH * t;
          return (
            <line
              key={t}
              x1={padX}
              y1={y}
              x2={padX + innerW}
              y2={y}
              stroke="var(--color-border)"
              strokeWidth="1"
              strokeDasharray={t === 1 ? "0" : "3 3"}
            />
          );
        })}
        {/* столбики запросов */}
        {reqBars.map((b, i) => (
          <rect
            key={i}
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            rx="2"
            fill="var(--color-brand-2)"
            opacity="0.85"
          >
            <title>{`${fmtHour(b.bucket)} · ${b.v} запр.`}</title>
          </rect>
        ))}
        {/* линия + area ошибок */}
        <path d={errArea} fill="url(#ts-err-fill)" />
        <path d={errPath} fill="none" stroke="var(--color-danger)" strokeWidth="1.6" strokeLinejoin="round" />
        {errPts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="2.2" fill="var(--color-danger)" />
        ))}
        {/* подписи x-оси */}
        {tickIdx.map((i) => {
          const x = padX + i * barW + barW / 2;
          return (
            <text
              key={i}
              x={x}
              y={H - 3}
              textAnchor="middle"
              fontSize="9"
              fill="var(--color-fg-muted)"
              fontFamily="IBM Plex Mono"
            >
              {fmtHour(data[i].bucket_start)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
